// In-app device self-test: an automated round-trip diagnostic against the live
// device. It reads the current device state, writes perturbed copies of it,
// verifies the device matches each exactly (write fidelity), then restores the
// original state. The live counterpart of completeness.test.ts — it exercises
// the real broker, not a mock, so it catches device-side divergence (clamping,
// ignored params, bulk-write drops) the unit tests cannot. Experimental.
//
// SAFETY: the perturbed plans are silent by construction. Every output level /
// head-amp gain is floored, the oscillator generator is forced off, and phantom
// power is forced off, so toggling channels / sends / routing cannot produce
// audible (or hot, or +48V) output while the test holds a perturbed state. Write
// fidelity for the level params is still exercised, at their (safe) minimum.
//
// COVERAGE: enum params are swept across passes — pass k selects option
// (k mod optionCount) for COMP/EQ type, COMP knee, OSC mode and EQ-band type, so
// every option is written over the run. Insert FX is swept on a single holder per
// pass (one input holder + one output bus), the rest set to none, to respect
// the device-wide 1-of-N slot exclusivity. The device auto-engages a
// (re)selected effect through its insert ON/OFF switch; that switch is modeled
// (insertFxOn) and emitted after the selector, so the restore puts a captured
// "selected but bypassed" effect back to bypassed. Input source is swept across
// the model's selectable input ports, so the (still-unverified on URX22/44)
// physical port map is exercised, not just the captured selection.
//
// The run is two blocks and every MONO IN pair's Signal Type is what separates them:
// unlinked first, then STEREO-linked (stereoLinkedPass). A linked pair is a different
// device state, not a variation of the same one — it holds ONE set of channel values
// between its two members and mirrors a write to either onto the other — so a run that
// stayed in one of them would leave the other unwritten. It is also what makes the
// per-channel insert-FX sweep expressible at all: a linked pair has one selector, so the
// unlinked block is the only place a channel can hold an effect its neighbour does not.
// Pass counts: unlinkedPassesFor is the max of the largest swept enum and the passes
// needed to reach every input port (so the trailing usbsub / hdmi ports are covered),
// linkedPassesFor one per (pair x option a linked pair may hold).
//
// THE RUN THEREFORE WRITES STRUCTURE, not only values. The capture holds the unit's own
// Signal Type / PAN-BAL and the restore writes them back, but a run that dies before its
// restore leaves the pairs in the block it was in, with the pans the unit slammed when it
// got there — so an interrupted run is a perturbed pair structure, not only perturbed
// values.
//
// UNVERIFIED GUESSES: some device mappings are confirmed on URX44V and URX22 and remain
// guesses on URX44 (see UNVERIFIED_MAPPINGS). A static audit first refutes any
// guessed id a confirmed param already owns — that outcome is `collision`, it settles
// the guess before anything is written, and its writes are suppressed so the test never
// misaddresses hardware. The rest are exercised, and each round-trip result is reported
// per guess: confirmed where a round trip answers the guess's question, round-tripped where
// it does not (`roundTripSettles`), refuted, or one of the three the round trip did not
// settle (see UnverifiedOutcome), so an owner can confirm them. This is the live
// counterpart of that confirmation workflow.

import type { DeviceModel } from "../../models/types";
import { parseRef, ref } from "../../models/types";
import type { Plan } from "../plan";
import { emptyPlan, PHANTOM_KEYS } from "../plan";
import { canConnect, isStereoLinkedPair, mirrorLinkedPair, partnerChannel } from "../routing";
import { vdConnect, vdDisconnect, vdGet, vdSet } from "../platform";
import {
  BUS_TYPE_OPTIONS,
  COMP_EQ_SSMCS,
  DELAY_FRAME_RATE_OPTIONS,
  EQ_ONE_KNOB_TYPE_OPTIONS,
  INSERT_FX_NONE,
  INSERT_FX_OPTIONS,
  insertFxAvailable,
  OUTPUT_INSERT_FX_OPTIONS,
  PAN_BAL_BAL,
  PAN_BAL_PAN,
  PARAMS,
  REC_POINT_OPTIONS,
  recPointOptionsFor,
} from "./params";
import type { ParamSpec } from "./params";
import {
  ClipSafeHold,
  diffNames,
  diffPlan,
  reachedAndFailed,
  sendConverging,
  sendNames,
  sendPresetsAndReconverge,
} from "./client";
import type { NameOutcome, SendOutcome } from "./client";
import { SETTLE_TIMEOUT_MS, writeSettle } from "./settle";
import { A_GAIN_MIN_DB, D_GAIN_MIN_DB } from "./vd";
import type { ConvergeRound } from "./client";
import { applyDeviceState } from "./readback";
import {
  auditUnverified,
  channelControl,
  cmdAddr,
  formatAddrKey,
  insertFxControl,
  planToCommands,
  selectableInputIds,
  UNVERIFIED_MAPPINGS,
  unverifiedAddresses,
} from "./translate";
import type { UnverifiedCollision, VdCommand } from "./translate";

export interface SelfTestMismatch {
  name: string;
  paramId: number;
  x: number;
  y: number;
  /** Value the plan wrote. */
  expected: number;
  /** What the device answered at the last read this pass made. On a pass that completed
   *  that is the read-back after the write; on one that stopped it is the comparison the
   *  run got to before it ended. Null when the address could not be read at that point. */
  actual: number | null;
  /** Sweep pass that produced this mismatch (-1 = the restore). */
  pass: number;
  /** How the PASS ended, when it did not finish — not a statement about this address.
   *  `sendCommands` stops at the first refusal, so an address earlier in the same round
   *  was sent, and a read that failed elsewhere says nothing about one that read fine.
   *  Absent on a pass that ran to the end of its loop — only those entries are a divergence the device settled on. On the rest the
   *  loop never finished converging, so the difference may be one a later round would have
   *  closed; printing them as a fidelity finding states a verdict the run did not reach.
   *  A model with no unverified mapping (URX44V) has no other guard: every one of them
   *  lands in the "other divergence" section, so one refused write read as the unit
   *  disagreeing about hundreds of parameters.
   *
   *  It does NOT mean the address went unread. `diffPlan` keeps reading past a failure
   *  (stopOnError is off here), so a residual entry is one that WAS read and did differ;
   *  the addresses that could not be read are absent from the residual entirely and are
   *  reported as read errors. */
  stoppedOn?: "read" | "write";
  /** Unverified-mapping key this address belongs to, if any (the guess it refutes). */
  unverifiedKey?: string;
}

/**
 * What the run has to say about one guessed mapping. `outcome` is stated, not derived:
 * it used to be two booleans with everything that was not `confirmed` falling through
 * to "refuted", so a guess the run could not READ was published as one the device had
 * contradicted — and every future reason for not-confirmed would have landed there too.
 * REFUTED is a claim about the device; it needs its own evidence.
 *
 *   confirmed   every address was written and read back unchanged in a pass that completed,
 *               for a guess whose question that answers
 *   roundTripped the same, for a guess whose question it does not answer — which channel an
 *               address reaches, or what a value means there (`roundTripSettles` false). A
 *               unit stores the value the same way whether the guess is right or wrong, so
 *               this is the most a run can say about it, and it is not a confirmation
 *   refuted     at least one did not, in a pass that completed — `mismatches` says which
 *   unread      one of ITS OWN addresses could not be read, so nothing about it round-
 *               tripped. This is the one per-address reason, taken from the failed reads
 *               themselves. Measured in a fixture: one unreadable address left an
 *               unrelated guess with 14 "mismatches"
 *   incomplete  it differed, in a pass that did not finish. WHY that pass stopped is a
 *               fact about the pass, not about this guess — `sendCommands` stops at the
 *               first refusal, so a guess written earlier in the same round was asked and
 *               answered; a read that failed elsewhere says nothing about an address that
 *               read fine. Naming the cause here printed exactly those two lies. The
 *               cause is in Issues, and the difference under "Not settled"
 *   unexercised not every one of its addresses was written and read back in a pass that
 *               completed, and no pass left one of them different or unread: the guess has
 *               none on this model, or the passes that wrote them all stopped — on a refused
 *               write or a failed read at another address — or the run was cancelled first
 *   collision   a confirmed param already owns the guessed id (static audit; the
 *               guess is wrong, and its writes were suppressed for safety)
 *
 * Evidence is per PASS, and that is what makes REFUTED durable: a pass that read
 * everything and still saw a divergence has settled the guess, and a read failure in
 * some later pass says nothing about the round trip that pass already observed.
 */
export type UnverifiedOutcome =
  "confirmed" | "roundTripped" | "refuted" | "unread" | "incomplete" | "unexercised" | "collision";

export interface UnverifiedFinding {
  key: string;
  label: string;
  outcome: UnverifiedOutcome;
  /** For a guess a round trip cannot settle, the observation that does — what the report
   *  prints beside every verdict but a refutation, which a round trip CAN reach. */
  howToSettle?: string;
  /** Addresses written for this guess that did not round-trip, from passes that
   *  completed. Non-empty exactly when the outcome is "refuted" — every other outcome is
   *  a statement that nothing was settled, and a list of differences printed under it
   *  reads as the evidence it is not. What a stopped pass left for this guess is in the
   *  report's "Not settled" section instead. */
  mismatches: SelfTestMismatch[];
}

/**
 * The converge trace of one pass that ended with a residual, plus the captured
 * baseline for the addresses that did not match. A residual line says a parameter
 * differs at the end; it cannot say whether the parameter was in the first round's
 * diff at all (it is not, when the device already held the plan's value and a later
 * command in the same round reset it), whether the following rounds re-sent it, or
 * how long each round took. That is the difference between a device that refuses a
 * write and one that takes it and then moves — which the summary cannot distinguish.
 */
/** One address's value in the captured device state. */
export interface CapturedValue {
  addr: string;
  name: string;
  value: number;
}

export interface SelfTestPassTrace {
  pass: number;
  /** What the capture holds at each residual address — the start of the diff the pass
   *  computed, without which the trace cannot say why a command was or was not sent
   *  in the first round. */
  baseline: CapturedValue[];
  rounds: ConvergeRound[];
}

export interface SelfTestReport {
  /** True when every pass matched its written plan exactly (no residual diff). */
  ok: boolean;
  /** Device model reported on connect. */
  device: string;
  /** Body-parameter groups read in the initial capture. */
  applied: number;
  /** Sweep passes run (each writes + verifies a perturbed plan). */
  passes: number;
  /** How many of them ran with the MONO IN pairs unlinked — the rest ran STEREO-linked.
   *  Carried on the report because a residual or a trace is read pass by pass, and which
   *  of the two states the pass was in is what says whether a channel could hold an
   *  insert effect of its own at all. */
  unlinkedPasses: number;
  /** Commands sent across all passes. */
  written: number;
  /** Params that did not match after a write — the findings. */
  residual: SelfTestMismatch[];
  /** Per-round trace of every pass that ended with a residual (see SelfTestPassTrace). */
  traces: SelfTestPassTrace[];
  /** Guessed ids that collide with a confirmed param (static audit; the guess is wrong). */
  collisions: UnverifiedCollision[];
  /** Per-unverified-mapping outcome; see UnverifiedOutcome for all seven. */
  unverified: UnverifiedFinding[];
  /** True when the user cancelled the run before it finished (remaining passes and
   *  the restore are skipped; the device is left in its last silent perturbed state). */
  aborted: boolean;
  /** True when the device was returned to its original captured state. */
  restored: boolean;
  /** Params that still differ from what the unit held before the run: the converging
   *  restore's residual PLUS the addresses that write has no command for or only a default
   *  for (the capture could not read them), read before the sweep and written back after
   *  it (restoreUnsent), PLUS the node names and Sweet
   *  Spot presets that still differ, or could not be read, after the string write-back. ⚠️ Still bounded by the app's
   *  parameter catalogue: a run also perturbs the unit's 1-knob base save-off, which
   *  has no entry and so cannot be read, written or counted (measured on a URX44V,
   *  2026-08-10 — 27 addresses; nothing observed restores from it, see diag and the
   *  private PLAN.md). */
  restoreResidual: number;
  /** Non-fatal issues (read failures, send failures) collected along the way. */
  errors: string[];
  /** Last phase reached — where it stopped if an error was thrown. "refused" is the
   *  one that is not a failure: the run declined to start and wrote nothing, so
   *  `restored` says nothing about the unit and callers must not read it as a failed
   *  restore (it is false because nothing was restored, not because something is left
   *  perturbed). */
  phase: "connect" | "readback" | "write" | "verify" | "restore" | "done" | "refused";
  /** Why a "refused" run declined: the connected unit is another model, the capture could not
   *  read a param whose write moves others (`ParamSpec.sideEffect`), or an address it would
   *  have to put back could not be read first. Absent on every other phase. */
  refusal?: "modelMismatch" | "sideEffectUnheld" | "unreadable";
  /**
   * DIAGNOSTIC — the restore's own account of itself, small enough to ride the report
   * line the headless launch already prints. It exists because a run that reported
   * `restoreResidual: 0` while an external sweep found 27 addresses changed left
   * nothing to read: neither the verdict nor the log could say whether the restore had
   * SENT those addresses, what the unit answered when it did, or what a later round
   * re-sent over the top. Each field answers exactly one of those.
   */
  diag: {
    /** Addresses the sweep wrote that the restore's command set does not carry. Empty
     *  means the restore does emit them, and the residue has another cause. */
    unrestorable: string[];
    /** Addresses where the captured plan would write something other than what the unit
     *  held before the sweep, or which that comparison could not read: a read the capture
     *  could not make leaves the plan's default there. Written back after the restore with
     *  `unrestorable`. */
    captureUnheld: string[];
    /** Per round of the converging restore: how much went out, how much of it was the
     *  PEQ and the 1-knob chain, and how much still differed on the re-read. A 1-knob
     *  group re-sent in the last round reloads the preset over the bands. */
    restoreRounds: Array<{
      sent: number;
      /** The round from its first send to the end of its re-read. */
      ms: number;
      bands: number;
      oneKnob: number;
      residual: number | null;
      /** The addresses the re-read still found different, as `name addr=device (want) #i`,
       *  where `i` is the address's position in this round's send order (`-` when unsent). */
      differing: string[];
      /** Every command in this round's send order whose param has a `sideEffect`, as
       *  `#i name addr=value` — what could have moved a differing address after it landed. */
      sideEffects: string[];
    }>;
    /** Band gains that disagreed with the captured value when read IMMEDIATELY after
     *  the restore. Empty here while an external sweep later finds them changed means
     *  the unit moved them after the write, not that the write never landed. */
    bandsAfterRestore: Array<{ addr: string; want: number; got: number | null }>;
  };
}

// What the restore emits, named once: the diagnostic below asks "which addresses will
// the restore NOT send", and asking it of a different set than the restore uses is how
// a run reports a gap it does not have (the PEQ was listed as unrestorable in a run
// that restored it, 2026-08-10).
const RESTORE_EMIT = { includeDeviceDriven: true } as const;

// The `pass` a residual entry or a trace carries when the restore, not a sweep pass, left it.
const RESTORE_PASS = -1;
const passLabel = (pass: number): string => (pass === RESTORE_PASS ? "restore" : `p${pass}`);

// Deep-negative dB that emit clamps to each level param's own minimum (-inf for
// faders / sends, the floor for gain / monitor), so the written state is silent.
const SILENCE_DB = -200;

// Enum params swept across passes (key → legal option values, in device order).
// COMP/EQ type is structural (it switches active GATE/COMP/EQ banks); sweeping it
// exercises both banks over the run. Every captured enum must be listed here so
// it cycles within its legal range — a blind +1 (the fallback for plain numbers)
// drives a 2-value enum like busType out of range, which the broker rejects.
// Driver toggles (oneKnob/autoMakeup), insertFx, EQ 1-knob type (its legal
// subset depends on the node) and Rec Point (its stages depend on the channel and
// its comp/EQ order — sweepRecPoint) are handled separately.
const ENUM_SWEEP: Record<string, number[]> = {
  compEqType: [0, 1],
  knee: [0, 1, 2],
  mode: [0, 1, 2],
  type: [0, 1, 2],
  busType: BUS_TYPE_OPTIONS.map((o) => o.value),
  frameRate: DELAY_FRAME_RATE_OPTIONS.map((o) => o.value),
};
// fxEffect / insertFxParams are skipped wholesale: their values are raw engine-
// array slots holding bounded enums and index tables (SP Type, Ratio index, Amp
// Type, MIDI bits, dB offsets, note numbers, …) plus sentinels, so the generic
// "+1 every number" perturb would write out-of-range values the device clamps or
// rejects (a false failure when a captured baseline has an effect assigned).
// fxEffect's type is per-FX (FX1 0..2/1024..1025, FX2 768..770/1024..1025), and
// both repopulate the array on a type/selector change (sideEffect). The selector
// itself (insertFx) is swept by sweepInsertFx; round-trip coverage for the
// engine slots lives in the completeness / value-coverage / insert-fx-effect /
// translate unit tests.
//
// stereoLink / panBal are out of the generic perturb and swept by sweepStereoLink
// instead: they are structural (Signal Type decides whether a pair holds one set of
// values or two, and resets the secondary channel on either transition), so what they
// need is a block per state rather than a per-pass flip — and panBal is a 0/1 enum only
// meaningful while linked, which the generic "+1" would drive out of range.
// comp.oneKnobLevel is a bounded 0..100 raw (not a small enum), so the "+1" nudge
// runs it past 100 when captured at max; skip it (its round-trip is value-covered).
const SKIP = new Set([
  "recPoint",
  "insertFx",
  "insertFxParams",
  "autoMakeup",
  "oneKnob",
  "oneKnobLevel",
  "fxEffect",
  "stereoLink",
  "panBal",
]);

// Enum-sweep floor: the max option-list length across every swept enum (today
// the 8 input insert-FX options), so every option of every swept enum is written
// at least once over the run. Exported for tests.
export const PASSES = Math.max(
  INSERT_FX_OPTIONS.length,
  OUTPUT_INSERT_FX_OPTIONS.length,
  EQ_ONE_KNOB_TYPE_OPTIONS.length,
  REC_POINT_OPTIONS.length,
  ...Object.values(ENUM_SWEEP).map((o) => o.length),
);

// The run is two blocks, and `stereoLinkedPass` is the boundary. Every MONO IN pair
// is forced UNLINKED for the first block and STEREO-linked for the second, because the
// two are different device states and a channel param reaches the unit through a
// different path in each: a linked pair holds ONE set of values between its two members
// and mirrors a write to either onto the other. A run that stayed in one of them would
// leave the other unexercised — and the sweep that stayed in whatever the unit happened
// to hold could not write a per-channel insert FX at all (a linked pair has one).
//
// Unlinked passes for a model: enough to write every swept enum option at least once AND
// to point some channel group at every selectable input port. The input-source
// sweep gives group gi the port at (pass + gi) mod N, so the union across G
// groups reaches every one of the N ports only after N - G + 1 passes (URX44V 10
// / URX44 9 / URX22 7). Taking the max with the enum floor keeps the last input
// ports (usbsub / hdmi) reached instead of leaving their INPUT_PORTS guess
// unexercised. Exported for tests.
export function unlinkedPassesFor(model: DeviceModel): number {
  const ports = selectableInputIds(model).length;
  return Math.max(PASSES, ports - channelSourceGroups(model).length + 1);
}

// Insert-FX options a STEREO-linked MONO IN pair may hold: the companders, which the
// user guide's Effect list says operate in stereo on such a pair, plus No Effect. The
// amps and Pitch Fix are `monoOnly` — the same field `insertFxMenu` locks as "link" —
// and although the broker takes one anyway, writing it here would be writing a state no
// surface of this app lets an operator choose.
const LINKED_INSERT_FX_OPTIONS = INSERT_FX_OPTIONS.filter((o) => !o.monoOnly);

// Linked passes for a model: one per (receiving pair x option), so every pair holds
// every option it can. The two cursors are coprime counts (pairs 2, options 3 — 1 and 3
// on a URX22), so k mod each reaches every combination in their product. Zero on a model
// with no MONO IN pair, which is no model today.
export function linkedPassesFor(model: DeviceModel): number {
  return model.channelPairs.length * LINKED_INSERT_FX_OPTIONS.length;
}

/** Whether sweep pass `pass` runs with every MONO IN pair STEREO-linked. Exported so the
 *  sweeps and their tests read the boundary from one place. */
export function stereoLinkedPass(model: DeviceModel, pass: number): boolean {
  return pass >= unlinkedPassesFor(model);
}

// Passes for a model: the unlinked block then the linked one. Exported for tests.
export function passesFor(model: DeviceModel): number {
  return unlinkedPassesFor(model) + linkedPassesFor(model);
}

// Perturb every scalar in an object tree in place: flip bools, nudge numbers,
// cycle the PRE/POST tap, and set swept enums to this pass's option.
function perturb(obj: Record<string, unknown>, pass: number): void {
  for (const [k, v] of Object.entries(obj)) {
    if (SKIP.has(k)) continue;
    if (k in ENUM_SWEEP && typeof v === "number") {
      const opts = ENUM_SWEEP[k];
      obj[k] = opts[pass % opts.length];
    } else if (typeof v === "boolean") {
      obj[k] = !v;
    } else if (typeof v === "number") {
      obj[k] = v + 1;
    } else if (typeof v === "string") {
      if (k === "tap") obj[k] = v === "pre" ? "post" : "pre";
    } else if (Array.isArray(v)) {
      for (const el of v) if (el && typeof el === "object") perturb(el as Record<string, unknown>, pass);
    } else if (v && typeof v === "object") {
      perturb(v as Record<string, unknown>, pass);
    }
  }
}

// The shared silence core: floor every level (creating connection params if absent,
// so a channel fader / send carries no signal even when the captured plan did not
// set it explicitly — kinds with no level ignore it), and disable the oscillator
// generator and phantom power. The audit-prep writer (prepare.ts) reuses this
// verbatim so the "silent by construction" contract lives in one place: prepare
// leaves its state on the device with no restore, so a drift here would ship a
// non-silent (or +48V) write to hardware.
export function floorSilent(plan: Plan): void {
  for (const np of Object.values(plan.nodeParams)) {
    if (typeof np.level === "number") np.level = SILENCE_DB;
    for (const key of PHANTOM_KEYS) if (np[key]) np[key] = false;
    if (np.osc) np.osc.on = false;
  }
  for (const c of plan.connections) c.params = { ...c.params, level: SILENCE_DB };
}

// Force the perturbed plan silent, applied last so it overrides perturb. A jack's A.Gain
// and a source's D.Gain are floored to their own minimum (not SILENCE_DB, which would be
// re-clamped and break the round trip): a still-unverified gain address could write to an
// unintended input, and the minimum is the safest value such a write can carry while still
// letting a correct address round-trip.
function makeSilent(plan: Plan): void {
  floorSilent(plan);
  for (const np of Object.values(plan.nodeParams)) {
    for (const key of ["gainL", "gainR"] as const) if (typeof np[key] === "number") np[key] = A_GAIN_MIN_DB;
    if (typeof np.dGain === "number") np.dGain = D_GAIN_MIN_DB;
  }
}

// Sweep Signal Type (stereo link) and PAN/BAL: every MONO IN pair takes this pass's
// block state, so both device states are exercised rather than whichever one the unit
// happened to be captured in. Written into the plan rather than as a step of its own,
// so the emit order carries it: translate puts SIGNAL_TYPE / PAN_BAL ahead of the pan
// blocks because either selector makes the unit slam the pair's CH_PAN and every bus
// send's pan, and the plan's pans have to land on top of that.
//
// PAN/BAL is the pair's own mode and only means anything while linked: the linked block
// alternates it so both are written, and the unlinked block holds PAN, which is what an
// unlinked pair reads. Writing BAL to an unlinked pair is a write nothing here has
// measured, and it is not what the app's own transition does either.
//
// Linking is not only a flag: a linked pair holds ONE set of channel values between its
// two members and mirrors a write to either onto the other, so a plan that still names two
// is asking for a state the unit cannot be in — the write sends both, the unit keeps the
// last, and the converge alternates. A capture taken while the pair was UNLINKED is free to
// disagree, which is exactly the state this block has to be able to write. So the primary's
// values are mirrored onto the secondary here, through the app's own definition of what a
// pair shares rather than a second list: mirrorLinkedPair leaves the head amp and — outside
// BAL — each member's pan alone, which is what the unit leaves alone too.
//
// It runs BEFORE sweepInsertFx, which then assigns the pass's effect to both members of a
// linked holder; the mirror carrying the captured selector across first changes nothing it
// does not overwrite.
//
// The pair's insert FX is NOT carried across the TRANSITION on the unit: it clears the
// selector and its ON on both members either way, and the sweep assigns this pass's effect
// afterwards.
function sweepStereoLink(plan: Plan, pass: number, model: DeviceModel): void {
  const linked = stereoLinkedPass(model, pass);
  const k = pass - unlinkedPassesFor(model);
  for (const [primary] of model.channelPairs) {
    const np = (plan.nodeParams[primary] ??= {});
    np.stereoLink = linked;
    np.panBal = linked && k % 2 === 0 ? PAN_BAL_BAL : PAN_BAL_PAN;
    if (linked) mirrorLinkedPair(model, plan, primary);
  }
}

// Sweep insert FX: one input holder and one output bus get this pass's option,
// every other insert-FX node is set to none. A single active effect per kind
// respects the device-wide 1-of-N slot exclusivity (two channels cannot hold the
// same slot at once), and options over the captured sample rate's ceiling are
// dropped (a rate-locked selector never round-trips), so the write is always
// legal. The receiving holder also rotates per pass, so every node/instance of the
// selector and switch params is exercised, not just the first. The ON/OFF
// (bypass) switch is modeled (insertFxOn, flipped by perturb): translate emits
// it after the selector on the effect-bearing nodes, so the device's auto-engage
// on (re)selection is overridden and both switch states round-trip over the run.
//
// An input HOLDER is a channel while its pair is unlinked and the PAIR once it is
// linked, because that is what the unit holds one effect for: a linked pair keeps one
// selector between its two members and mirrors a write to either onto the other. Giving
// one member an effect and the other No Effect describes no state the unit can be in —
// the write sends two selectors, the unit keeps whichever landed last, and the converge
// alternates to its round limit and finishes unconverged. plan-validate.ts's
// insertFxPairProblems refuses the same document at the load. So a linked holder is
// written to BOTH its members, and the linked block picks from the options such a pair
// may hold.
function sweepInsertFx(plan: Plan, pass: number, model: DeviceModel): void {
  const inputs: string[][] = [];
  const outputs: string[] = [];
  const paired = new Set<string>();
  for (const node of model.nodes) {
    const ifx = insertFxControl(model, node.id);
    if (!ifx) continue;
    // Reset the selector and drop any captured effect params: a fresh effect
    // populates its own per-type defaults on the device, and emitting another
    // effect's stale slots would write nonsense to the new engine.
    const np = (plan.nodeParams[node.id] ??= {});
    np.insertFx = INSERT_FX_NONE;
    delete np.insertFxParams;
    if (ifx.isOutput) {
      outputs.push(node.id);
      continue;
    }
    if (paired.has(node.id)) continue;
    const partner = isStereoLinkedPair(model, plan, node.id) ? partnerChannel(model, node.id) : undefined;
    if (partner !== undefined) paired.add(partner);
    inputs.push(partner !== undefined ? [node.id, partner] : [node.id]);
  }
  const linked = stereoLinkedPass(model, pass);
  const k = linked ? pass - unlinkedPassesFor(model) : pass;
  const legalIn = (linked ? LINKED_INSERT_FX_OPTIONS : INSERT_FX_OPTIONS).filter((o) =>
    insertFxAvailable(o, plan.sampleRate),
  );
  const legalOut = OUTPUT_INSERT_FX_OPTIONS.filter((o) => insertFxAvailable(o, plan.sampleRate));
  if (inputs.length) {
    const value = legalIn[k % legalIn.length].value;
    for (const id of inputs[k % inputs.length]) plan.nodeParams[id].insertFx = value;
  }
  if (outputs.length) {
    plan.nodeParams[outputs[pass % outputs.length]].insertFx = legalOut[pass % legalOut.length].value;
  }
}

// Group channels into source-selection units: a linked mono pair, or a single
// channel (unpaired mono / stereo). Shared by the input-source sweep (which
// assigns each group an input node) and the pass count (which needs the group
// count, G, to size full port coverage).
function channelSourceGroups(model: DeviceModel): string[][] {
  const groups: string[][] = [];
  const seen = new Set<string>();
  for (const node of model.nodes) {
    if (node.kind !== "channel" || seen.has(node.id)) continue;
    const partner = partnerChannel(model, node.id);
    if (partner && model.nodes.some((n) => n.id === partner)) {
      groups.push([node.id, partner]);
      seen.add(node.id).add(partner);
    } else {
      groups.push([node.id]);
      seen.add(node.id);
    }
  }
  return groups;
}

// Sweep input-source selection across the model's selectable input ports, so the
// physical port map (INPUT_PORTS) is verified — not just whatever the captured
// state happened to assign. Each pass points every channel group at a different
// input node (cycling), replacing its source wire. A linked mono pair is assigned
// together to one node (the device fixes the partner: its L port to the first
// channel, R to its partner), and a stereo channel takes one node's L/R pair —
// matching how planToCommands derives the per-slot port. The port value written is
// inputPorts(node), so a mismatch on read-back pins INPUT_PORTS as wrong. Levels
// are floored by makeSilent, so re-routing inputs stays silent.
export function sweepInputSource(plan: Plan, pass: number, model: DeviceModel): void {
  const inputs = selectableInputIds(model);
  if (!inputs.length) return;
  const groups = channelSourceGroups(model);
  const channelIds = new Set(groups.flat());
  // Drop existing channel source wires (other "source" wires — stream / monitor —
  // are left alone), then assign each group one cycling input node.
  plan.connections = plan.connections.filter((c) => c.kind !== "source" || !channelIds.has(parseRef(c.to).nodeId));
  groups.forEach((group, gi) => {
    const inputId = inputs[(pass + gi) % inputs.length];
    for (const chId of group) {
      const from = ref(inputId, "out");
      const to = ref(chId, "in");
      if (canConnect(model, plan, from, to).ok) plan.connections.push({ from, to, kind: "source" });
    }
  });
}

/** Give each captured channel's Rec Point one of the stages that channel offers this pass —
 *  the stereo channels their two, a MONO IN its five, or four while in SSMCS mode. */
function sweepRecPoint(plan: Plan, pass: number, model: DeviceModel): void {
  for (const node of model.nodes) {
    const np = plan.nodeParams[node.id];
    const cc = channelControl(model, node.id);
    if (!cc || np?.recPoint === undefined) continue;
    const ssmcs = cc.hasMicStrip && np.compEqType === COMP_EQ_SSMCS;
    const stages = recPointOptionsFor(!cc.hasMicStrip, ssmcs);
    np.recPoint = stages[pass % stages.length].value;
  }
}

/**
 * Build the (silent) perturbed plan for a given sweep pass. `suppress` holds the
 * keys of colliding guesses to drop: each such mapping strips its own plan field
 * (the confirmed param sharing that address is still written, so it stays
 * covered). Exported for tests.
 */
export function perturbedPlan(model: DeviceModel, original: Plan, pass: number, suppress?: ReadonlySet<string>): Plan {
  const plan = structuredClone(original);
  for (const np of Object.values(plan.nodeParams)) perturb(np as Record<string, unknown>, pass);
  for (const c of plan.connections) if (c.params) perturb(c.params as Record<string, unknown>, pass);
  // After the comp/EQ order has moved, which decides the stages a MONO IN offers.
  sweepRecPoint(plan, pass, model);
  // Before the insert-FX sweep, which reads the link state to decide what a holder is.
  sweepStereoLink(plan, pass, model);
  sweepInsertFx(plan, pass, model);
  sweepInputSource(plan, pass, model);
  if (suppress) for (const m of UNVERIFIED_MAPPINGS) if (suppress.has(m.key)) m.suppress?.(plan);
  makeSilent(plan);
  return plan;
}

/**
 * The connect-time capture shared by the self-test and the audit-prep writer
 * (prepare.ts): verify the just-connected device matches `model`, then read its
 * current state into a fresh plan. Returns whether the model matched (`ok`), the
 * captured plan, and the read's applied count / errors — the model-mismatch
 * message is already in `errors` — plus the required receivers whose source the read did
 * not establish (`ReadbackResult.sourceUnread`). The caller owns connect + disconnect and
 * its own cancel handling: a cancel throws out of applyDeviceState and propagates.
 */
export async function captureDeviceState(
  model: DeviceModel,
  deviceModel: string,
  signal?: AbortSignal,
): Promise<{
  ok: boolean;
  plan: Plan;
  applied: number;
  errors: string[];
  sourceUnread: string[];
  unreadNodes: ReadonlySet<string>;
}> {
  const plan = emptyPlan(model.id);
  if (deviceModel !== model.id) {
    return {
      ok: false,
      plan,
      applied: 0,
      errors: [`connected device is ${deviceModel}, not ${model.id}`],
      sourceUnread: [],
      unreadNodes: new Set(),
    };
  }
  const r = await applyDeviceState(model, plan, signal);
  return {
    ok: true,
    plan,
    applied: r.applied,
    errors: r.errors,
    sourceUnread: r.sourceUnread ?? [],
    unreadNodes: r.unreadNodes,
  };
}

/**
 * Run the device round-trip self-test. Connects, captures the device state, then
 * for each sweep pass writes a (silent) perturbed copy and verifies the device
 * matches it; finally restores the original — always disconnecting. Read/send
 * failures are collected into the report rather than thrown; a thrown error
 * leaves `phase` at the failing step. The caller must ensure the connected
 * device matches `model`.
 */
export async function runSelfTest(
  model: DeviceModel,
  settleMs = SETTLE_TIMEOUT_MS,
  signal?: AbortSignal,
): Promise<SelfTestReport> {
  const passes = passesFor(model);
  const report: SelfTestReport = {
    ok: false,
    device: "",
    applied: 0,
    passes,
    unlinkedPasses: unlinkedPassesFor(model),
    written: 0,
    residual: [],
    traces: [],
    collisions: [],
    unverified: [],
    aborted: false,
    restored: false,
    restoreResidual: 0,
    errors: [],
    phase: "connect",
    diag: { unrestorable: [], captureUnheld: [], restoreRounds: [], bandsAfterRestore: [] },
  };
  // Run one phase's round-trips, returning its result — or undefined if the user
  // cancelled (the inner loops throw via signal.throwIfAborted). A cancel is
  // recorded on the report and swallowed so the caller can bail; anything else
  // rethrows. Centralizes the "stop on our abort, propagate real errors" contract.
  const phaseStep = async <T>(work: Promise<T>): Promise<T | undefined> => {
    try {
      return await work;
    } catch (e) {
      if (signal?.aborted) {
        report.aborted = true;
        return undefined;
      }
      throw e;
    }
  };
  // Static audit (no device): guessed ids a confirmed param already owns are wrong
  // and must not be written. Their writes are suppressed; they are reported as
  // collisions so an owner knows the guess is refuted before any hardware round-trip.
  report.collisions = auditUnverified(model.id);
  const suppress = new Set(report.collisions.map((c) => c.key));
  // Reads the run could not make. They are not mismatches — nobody knows whether the
  // parameter matched — so they cannot go in `residual`, and a verdict that ignores
  // them claims a fidelity it did not measure.
  let readFailures = 0;
  // Unverified-mapping keys with at least one address the run could not read. A read
  // failure keeps the command out of the diff (diffPlan) and so out of the residual, so
  // without this the guess shows no mismatch and reports CONFIRMED — a promotion to
  // "verified on hardware" for an address nothing round-tripped.
  const unreadKeys = new Set<string>();
  // Unverified-mapping keys a pass refuted on evidence it can stand behind: the address
  // diverged, in a pass whose reads all succeeded. Accumulated here, pass by pass, rather
  // than derived afterwards from run-wide counters — those cannot say WHICH pass a
  // residual came from, so one late read failure would retract a refutation an earlier
  // pass had already established.
  const refutedKeys = new Set<string>();
  // …and keys whose only divergence came from a pass that did NOT complete. No cause is
  // carried: how the pass ended is a fact about the pass, and attributing it to each
  // address in the residual claims things the run did not observe (see UnverifiedOutcome).
  const stoppedKeys = new Set<string>();
  // Guess addresses a pass that completed wrote and read: what `confirmed` and
  // `roundTripped` rest on. A registered address no pass emitted was never asked about.
  const compared = new Set<string>();
  // Commands the device reached and refused. `residual` covers the same runs today, but
  // via a coupling in sendConverging rather than as a stated fact (see report.ok).
  let sendFailures = 0;
  // Address → unverified-mapping key, so each residual can be tagged with the guess
  // it refutes. A colliding guess shares its address with a confirmed param (whose
  // write is kept); drop those entries so a residual there is attributed to the
  // confirmed param, not mislabeled as the suppressed guess.
  const addresses = unverifiedAddresses(model);
  for (const [addr, key] of addresses) if (suppress.has(key)) addresses.delete(addr);
  const device = await vdConnect(model.id);
  report.device = device.model;
  try {
    // 1. Capture the current device state (the connect prologue shared with prepare.ts).
    report.phase = "readback";
    const cap = await phaseStep(captureDeviceState(model, device.model, signal));
    if (!cap) return report; // cancelled during capture
    report.applied = cap.applied;
    report.errors.push(...cap.errors);
    // Connected device is not this model: nothing read, nothing written.
    if (!cap.ok) {
      report.phase = "refused";
      report.refusal = "modelMismatch";
      return report;
    }
    const original = cap.plan;
    // A STREAMING source the capture did not read is written by nothing in the run: with no
    // wire there the emit sends nothing to its selector, so no pass moves it, the restore has
    // nothing to put back, and the unit keeps the source it held.
    for (const id of cap.sourceUnread) {
      original.connections = original.connections.filter((c) => c.to !== ref(id, "in"));
      report.errors.push(
        `${id}: source not captured, so no pass and not the restore writes it; the unit keeps its own`,
      );
    }
    // The capture as device values, for a failing pass's trace: the same inverse the
    // restore writes, indexed once rather than per pass.
    const captured = new Map<number, CapturedValue>();
    for (const c of planToCommands(model, original, "all", RESTORE_EMIT)) {
      captured.set(cmdAddr(c), { addr: formatAddrKey(cmdAddr(c)), name: c.name, value: c.vdValue });
    }
    // Addresses a pass emits that the captured plan does not: the restore has no command
    // for them, so it cannot put them back and its residual cannot speak for them either.
    // They are read off the unit here, BEFORE anything is perturbed, and written back
    // after the restore.
    //
    // A plan emits an address only under the mode that owns it, and the sweep runs in
    // every mode: the SSMCS GATE/COMP/EQ section toggles exist only in the other COMP/EQ
    // order, and the insert-FX ON switch only while an effect is selected. Every one of
    // them is a confirmed param the app already drives, so writing it back is ordinary —
    // what makes them special is only that the captured plan has no command for them.
    //
    // The 4-band PEQ used to be a third family here and is not any more: it is handled
    // where it belongs, by the restore asking for it (EmitOptions.includeDeviceDriven).
    report.phase = "readback";
    const unrestorable = new Map<number, VdCommand>();
    for (let pass = 0; pass < passes; pass++) {
      for (const c of planToCommands(model, perturbedPlan(model, original, pass, suppress))) {
        if (!captured.has(cmdAddr(c))) unrestorable.set(cmdAddr(c), c);
      }
    }
    // Addresses the captured plan names but holds no reading for. A read the capture could
    // not make leaves the plan's own default there — no source on a selector, unity on a
    // fader, -inf on a send — and the restore would converge the unit onto that default. A
    // diff of the captured plan against the unit, before anything is perturbed, finds each
    // of them whichever group the failed read was in, and they join the addresses above:
    // read before the sweep, written back after the restore.
    const held = await phaseStep(diffPlan(model, original, { signal, emit: RESTORE_EMIT }));
    if (!held) return report; // cancelled during the diff
    const writeBack = new Map(unrestorable);
    for (const command of [...held.diffs.map((d) => d.command), ...held.unread]) {
      writeBack.set(cmdAddr(command), command);
      report.diag.captureUnheld.push(`${command.name} ${formatAddrKey(cmdAddr(command))}`);
    }
    // A head among them on a node the capture could not read — an insert-FX selector, Signal
    // Type, any param whose write moves others — would be written back after the restore,
    // whether the captured plan holds a default there or sends nothing there at all, and what
    // that write refills or resets on the unit is then compared by nothing. The run does not
    // start.
    const unheldHeads = [...writeBack.values()]
      .filter(
        (c) =>
          (PARAMS as Record<string, ParamSpec>)[c.name]?.sideEffect !== undefined &&
          c.node !== undefined &&
          cap.unreadNodes.has(c.node),
      )
      .map((c) => `${c.name} ${formatAddrKey(cmdAddr(c))}`);
    if (unheldHeads.length) {
      report.errors.push(
        `refusing to sweep: the capture could not read ${unheldHeads.length} param(s) whose write moves others: ${unheldHeads.join(", ")}`,
      );
      report.phase = "refused";
      report.refusal = "sideEffectUnheld";
      return report;
    }
    // Through phaseStep like every other await here: a cancel inside this loop is a
    // cancel, and without it the abort escapes runSelfTest and reaches the user as a
    // self-test ERROR dialog instead.
    const preSweep = await phaseStep(readPreSweep(writeBack, signal, report));
    if (!preSweep) return report; // cancelled during the pre-sweep read
    // An address in this set has no other record of what it held: the captured plan has
    // no command for it, or a default where the unit's value should be, so a read failure
    // here means the run could perturb it and never put it back. Nothing has been written
    // yet, so the honest answer is not to start — counting it in the residual afterwards
    // leaves the unit changed and only says so. `errors` already names each one.
    if (preSweep.size !== writeBack.size) {
      report.errors.push(
        `refusing to sweep: ${writeBack.size - preSweep.size} address(es) the restore cannot put back could not be read first`,
      );
      report.phase = "refused";
      report.refusal = "unreadable";
      return report;
    }

    // 2. Sweep: each pass writes a silent perturbed plan (converging, so params
    // the device resets as a side effect of a mode change are re-sent) and the
    // residual after convergence is the pass's mismatches. Cancellation stops
    // issuing round-trips between commands; the in-flight one finishes (so the
    // device is left consistent), then sendConverging throws and phaseStep bails.
    for (let pass = 0; pass < passes; pass++) {
      const plan = perturbedPlan(model, original, pass, suppress);
      report.phase = "write";
      const result = await phaseStep(
        sendConverging(model, plan, { settleMs, signal, trace: true, stopOnError: false }),
      );
      if (!result) break;
      const { outcomes, residual } = result;
      // Reached the device — not `outcomes.length`, which also counts the commands
      // `sendCommands` marked skipped after the first refusal. Those never left the app,
      // and counting them had the same report saying "N commands written" and "M
      // command(s) never sent" about the same run.
      report.written += outcomes.filter((o) => !o.skipped).length;
      sendFailures += outcomes.filter(reachedAndFailed).length;
      report.errors.push(...sendFailureLines(outcomes, `p${pass}`));
      // A command whose current value could not be read is left out of the diff by
      // design (diffPlan: never write on a value you did not confirm), which also leaves
      // it out of the residual — so without this it reads as a parameter that matched,
      // and it also ended the converge loop early.

      report.errors.push(...result.readErrors.map((e) => `p${pass} read: ${e}`));
      readFailures += result.readErrors.length;
      for (const c of result.unread) {
        const key = addresses.get(`${c.paramId}:${c.y}`);
        if (key) unreadKeys.add(key);
      }

      report.phase = "verify";
      // Keep the trace of a pass that did not converge, with the captured state of
      // the addresses that stayed wrong: the summary below records the end state,
      // and this is what says how it got there.
      if (residual.length) {
        const baseline = residual.map((d) => captured.get(cmdAddr(d.command))).filter((v) => v !== undefined);
        report.traces.push({ pass, baseline, rounds: result.trace });
      }
      // How THIS pass ended, which is what decides whether its residual is evidence.
      // Only a pass that ran to the end of its own loop watched each address converge or
      // fail to; both ways out short of that leave a residual describing the run rather
      // than the device, and they are not the same thing to report. The send failure is
      // the worse of the two — sendConverging breaks out WITHOUT re-reading, so what it
      // hands back is the diff it was about to write, i.e. every address the pass
      // intended to change.
      const stoppedOn: "read" | "write" | null = result.readErrors.length
        ? "read"
        : outcomes.some(reachedAndFailed)
          ? "write"
          : null;
      if (stoppedOn === null) {
        const unread = new Set(result.unread.map((c) => cmdAddr(c)));
        for (const c of planToCommands(model, plan)) {
          const guessAddr = `${c.paramId}:${c.y}`;
          if (c.x === 0 && addresses.has(guessAddr) && !unread.has(cmdAddr(c))) compared.add(guessAddr);
        }
      }
      for (const d of residual) {
        const unverifiedKey = d.command.x === 0 ? addresses.get(`${d.command.paramId}:${d.command.y}`) : undefined;
        if (unverifiedKey) {
          if (stoppedOn === null) refutedKeys.add(unverifiedKey);
          else stoppedKeys.add(unverifiedKey);
        }
        report.residual.push({
          name: d.command.name,
          paramId: d.command.paramId,
          x: d.command.x,
          y: d.command.y,
          expected: d.command.vdValue,
          actual: d.current,
          pass,
          unverifiedKey,
          ...(stoppedOn ? { stoppedOn } : {}),
        });
      }
    }
    // A refused write is stated here rather than inferred from the residual. It does
    // always leave one (sendConverging only enters a round while something differs), so
    // the term is redundant today — but that is a coupling two functions away, and `ok`
    // is the field a caller reads to decide the unit is fine.
    report.ok = !report.aborted && report.residual.length === 0 && readFailures === 0 && sendFailures === 0;

    // Per-guess verdict. Each branch is a reason, in the order that decides: a collision
    // is known wrong before any hardware; a divergence a complete pass observed settles
    // the guess and nothing later takes it back; short of that, one of its own addresses
    // was unreadable, or it differed in a pass that did not finish — and only the first
    // of those two is a fact about the guess; a guess whose every address a completed pass
    // wrote and read held is confirmed where a round trip answers its question and
    // round-tripped where it does not; anything short of every address was never tested.
    const exercised = (key: string): boolean => {
      const own = [...addresses].filter(([, k]) => k === key).map(([addr]) => addr);
      return own.length > 0 && own.every((addr) => compared.has(addr));
    };
    report.unverified = UNVERIFIED_MAPPINGS.filter((m) => m.models.includes(model.id)).map((m) => {
      // Only what a completed pass observed. The outcome already turns on that, but the
      // DETAIL did not: a guess refuted in pass 0 also collected every stopped pass's
      // residual, so the report published "REFUTED — N address(es)" over a count and a
      // wrote/read list padded with differences nothing had settled.
      const mismatches = report.residual.filter((r) => r.unverifiedKey === m.key && !r.stoppedOn);
      const outcome: UnverifiedOutcome = suppress.has(m.key)
        ? "collision"
        : refutedKeys.has(m.key)
          ? "refuted"
          : unreadKeys.has(m.key)
            ? "unread"
            : stoppedKeys.has(m.key)
              ? "incomplete"
              : exercised(m.key)
                ? m.roundTripSettles
                  ? "confirmed"
                  : "roundTripped"
                : "unexercised";
      return {
        key: m.key,
        label: m.label,
        outcome,
        mismatches,
        ...(!m.roundTripSettles && m.howToSettle ? { howToSettle: m.howToSettle } : {}),
      };
    });

    // 3. Restore the original state (converging, for the same reset behavior).
    // Skipped on cancel: the device is left silent (safe by makeSilent), and a
    // restore would only re-run the round-trips the user just cancelled.
    if (!report.aborted) {
      report.phase = "restore";
      report.diag.unrestorable = [...unrestorable.values()].map((c) => `${c.name} ${formatAddrKey(cmdAddr(c))}`);
      // includeDeviceDriven widens the restore — and only the restore — to the bands
      // the app never authors while EQ 1-knob is on, because the unit drives them. A
      // capture holding 1-knob ON otherwise emits none of them while every sweep pass
      // wrote them, so the unit ends with a VISIBLE EQ change under a verdict that says
      // it was restored. The unit keeps such a write (measured; see EmitOptions), and
      // the residual now covers the same set, so a unit that did re-derive would be
      // reported rather than passed over.
      const restore = { settleMs, signal, trace: true, emit: RESTORE_EMIT, stopOnError: false };
      const numeric = await phaseStep(sendConverging(model, original, restore));
      // Then the captured Sweet Spot presets, which the converge cannot write, and the strip they
      // rebuild converged again behind them (client.ts sendPresetsAndReconverge). One round of
      // the sweep's pair links is undone inside the converge above, and that puts a preset of the
      // link's choosing back on the secondary when it lands.
      const presets =
        numeric && !numeric.outcomes.some(reachedAndFailed) && !numeric.readErrors.length
          ? await phaseStep(sendPresetsAndReconverge(model, original, restore))
          : { presets: [], notSent: [], unconfirmed: [], canceled: false, readErrors: [], result: null };
      if (!presets) return report;
      report.errors.push(...nameFailureLines(presets.presets));
      // A refused preset stops the restore's writes there. The nodes whose preset was accepted
      // had their strip rebuilt with nothing reading it back, so their state is unknown.
      for (const node of presets.unconfirmed)
        report.errors.push(
          `restore ${node}: its Sweet Spot preset rebuilt the strip, and the restore stopped before reading it back`,
        );
      // A cancel once a preset has gone out ends the run the way a cancel anywhere else in the
      // restore does, with what went out already in the report.
      if (presets.canceled) {
        report.aborted = true;
        return report;
      }
      const presetRefused = presets.presets.some((o) => !o.ok);
      report.errors.push(...presets.readErrors.map((e) => `restore name read: ${e}`));
      const back =
        numeric && presets.result
          ? {
              ...presets.result,
              outcomes: [...numeric.outcomes, ...presets.result.outcomes],
              readErrors: [...numeric.readErrors, ...presets.result.readErrors],
              trace: [...numeric.trace, ...presets.result.trace],
            }
          : numeric;
      if (back) {
        report.diag.restoreRounds = back.trace.map((r) => {
          const position = new Map(r.sent.map((c, i) => [cmdAddr(c), i] as const));
          return {
            sent: r.sent.length,
            ms: r.elapsedMs,
            bands: r.sent.filter((c) => c.name.startsWith("EQ_BAND")).length,
            oneKnob: r.sent.filter((c) => c.name.startsWith("EQ_ONE_KNOB")).length,
            residual: r.reread?.length ?? null,
            differing: (r.reread ?? []).map(
              (d) =>
                `${d.command.name} ${formatAddrKey(cmdAddr(d.command))}=${d.current} (${d.command.vdValue}) #${position.get(cmdAddr(d.command)) ?? "-"}`,
            ),
            sideEffects: r.sent.flatMap((c, i) =>
              (PARAMS as Record<string, ParamSpec>)[c.name]?.sideEffect
                ? [`#${i} ${c.name} ${formatAddrKey(cmdAddr(c))}=${c.vdValue}`]
                : [],
            ),
          };
        });
        // Read back the band gains the restore just wrote, before anything else can
        // touch the unit. RESTORE_EMIT, not the default: the default omits exactly the
        // bands this exists to watch — the ones under EQ 1-knob — so asking with it
        // inspects nothing in the one configuration that motivated the field.
        if (!(await phaseStep(probeBands(model, original, signal, report)))) return report;
        report.restoreResidual = back.residual.length;
        // Each address the restore left different, itemized with its trace the way a
        // sweep pass's residual is: a count alone names nothing to put back by hand.
        const restoreStoppedOn: "read" | "write" | null = back.readErrors.length
          ? "read"
          : back.outcomes.some(reachedAndFailed)
            ? "write"
            : null;
        for (const d of back.residual) {
          report.residual.push({
            name: d.command.name,
            paramId: d.command.paramId,
            x: d.command.x,
            y: d.command.y,
            expected: d.command.vdValue,
            actual: d.current,
            pass: RESTORE_PASS,
            ...(restoreStoppedOn ? { stoppedOn: restoreStoppedOn } : {}),
          });
        }
        if (back.residual.length) {
          const baseline = back.residual.map((d) => captured.get(cmdAddr(d.command))).filter((v) => v !== undefined);
          report.traces.push({ pass: RESTORE_PASS, baseline, rounds: back.trace });
        }
        // Same for the restore, and it matters more here: a read failure also ENDS the
        // converge loop (sendConverging stops on one), so the write may have stopped
        // part-way. Counting them keeps `restored` a statement about what was checked.
        report.errors.push(...back.readErrors.map((e) => `restore read: ${e}`));
        report.restoreResidual += back.readErrors.length;
        // A refused WRITE ends the same loop, and without this the report says only that
        // N params differ — which reads as a unit that would not keep the values, when
        // the values never reached it. Not added to restoreResidual: the residual it left
        // is the pre-write diff, so those params are already counted there.
        report.errors.push(...sendFailureLines(back.outcomes, "restore"));

        // Then the addresses that write has no command for, or only the captured plan's
        // default for, put back from what the unit held before the sweep. Last, so the
        // converging write's side-effect resets have already landed. After a refused preset nothing more is written: they are only
        // read, and what still differs is counted as not put back.
        const unsent = await phaseStep(
          restoreUnsent(writeBack, preSweep, settleMs, signal, report, { writeBack: !presetRefused }),
        );
        if (unsent === undefined) return report; // cancelled during the write-back
        report.restoreResidual += unsent;
        // The string half: what the converge cannot write, verified the way it verifies its
        // own. A name the first read could not see is counted too, since nothing put it back.
        if (!presetRefused) {
          const names = await phaseStep(diffNames(model, original));
          if (!names) return report;
          const renamed = await phaseStep(sendNames(names.writes.filter((w) => w.name === undefined)));
          if (!renamed) return report;
          report.errors.push(...nameFailureLines(renamed));
        }
        const namesAfter = await phaseStep(diffNames(model, original));
        if (!namesAfter) return report;
        report.errors.push(...namesAfter.errors.map((e) => `restore name verify: ${e}`));
        for (const w of namesAfter.writes)
          report.errors.push(`restore ${w.param}:0:${w.y}: still differs from ${JSON.stringify(w.value)}`);
        report.restoreResidual +=
          presets.readErrors.length + presets.unconfirmed.length + namesAfter.errors.length + namesAfter.writes.length;
        report.restored = report.restoreResidual === 0;
        report.phase = "done";
      }
    }
    return report;
  } finally {
    await vdDisconnect(device.epoch);
  }
}

/**
 * Read what the unit holds at the addresses the restore has no command for, before
 * anything perturbs them. An address that cannot be read is left OUT of the result: the
 * run then neither writes it back nor claims it did, and the caller counts the shortfall
 * into the residual. Recording it as readable-but-unknown would be the worse shape —
 * restoreUnsent would skip it silently and the verdict would not notice.
 */
async function readPreSweep(
  unrestorable: Map<number, VdCommand>,
  signal: AbortSignal | undefined,
  report: SelfTestReport,
): Promise<Map<number, number>> {
  const preSweep = new Map<number, number>();
  for (const [addr, c] of unrestorable) {
    signal?.throwIfAborted();
    try {
      preSweep.set(addr, await vdGet(c.paramId, c.x, c.y));
    } catch (e) {
      report.errors.push(`pre-sweep read ${formatAddrKey(addr)}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  return preSweep;
}

/**
 * Read the band gains the restore just wrote, before anything else can touch the unit,
 * and keep only the ones that disagree. Empty here while a later external sweep finds
 * them changed is what separates "the write never went" from "the unit moved it after".
 */
async function probeBands(
  model: DeviceModel,
  original: Plan,
  signal: AbortSignal | undefined,
  report: SelfTestReport,
): Promise<true> {
  for (const c of planToCommands(model, original, "all", RESTORE_EMIT)) {
    if (c.name !== "EQ_BAND_GAIN") continue;
    signal?.throwIfAborted();
    try {
      const got = await vdGet(c.paramId, c.x, c.y);
      if (got !== c.vdValue)
        report.diag.bandsAfterRestore.push({ addr: formatAddrKey(cmdAddr(c)), want: c.vdValue, got });
    } catch {
      report.diag.bandsAfterRestore.push({ addr: formatAddrKey(cmdAddr(c)), want: c.vdValue, got: null });
    }
  }
  return true;
}

/** Refused string writes, as report lines. */
function nameFailureLines(outcomes: readonly NameOutcome[]): string[] {
  return outcomes.filter((o) => !o.ok).map((o) => `restore ${o.write.param}:0:${o.write.y}: ${o.error}`);
}

/**
 * Send failures from one converging write, as report lines. `sendCommands` stops at the
 * first command the device refused and marks every command after it `skipped`, so the
 * raw !ok list is one real failure trailed by however many never left the app — each of
 * which would print with an undefined message. The refusal is the line worth reading;
 * what it stopped is a count.
 */
function sendFailureLines(outcomes: readonly SendOutcome[], prefix: string): string[] {
  const lines = outcomes.filter(reachedAndFailed).map((o) => `${prefix} ${o.command.name}: ${o.error}`);
  const skipped = outcomes.filter((o) => o.skipped).length;
  if (skipped) lines.push(`${prefix} ${skipped} command(s) never sent — the write stopped at the failure above`);
  return lines;
}

/**
 * Write back the addresses the converging restore has no command for, or writes a default
 * to because the capture could not read them, and report how many did not take. `before` is what the unit answered for each ahead of the sweep; an
 * address missing from it was unreadable then, so there is nothing to put back and its
 * failure is already in `errors`.
 *
 * Verified by re-reading past the settle window rather than by trusting the ack: a write
 * is not readable while it is acked, and a read inside that window answers the old value
 * — which would report a restoration that worked as one that failed. Returns the count
 * that still differs, which the caller adds to the residual, so a unit that insists on
 * its own value lands there instead of being passed over. With `writeBack` false nothing is
 * written and the addresses are only read, so each one still off its pre-sweep value counts.
 */
async function restoreUnsent(
  unrestorable: Map<number, VdCommand>,
  before: Map<number, number>,
  settleMs: number,
  signal: AbortSignal | undefined,
  report: SelfTestReport,
  { writeBack }: { writeBack: boolean },
): Promise<number> {
  if (!before.size) return 0;
  if (writeBack) {
    // A Clip Safe OFF written back behind the same jack's A.Gain waits for that A.Gain's
    // announcement, as every other sender's does.
    const hold = new ClipSafeHold(settleMs);
    for (const [addr, c] of unrestorable) {
      const want = before.get(addr);
      if (want === undefined) continue;
      signal?.throwIfAborted();
      try {
        await hold.before(c, want, signal);
        hold.sending(c, want, writeSettle.mark());
        await vdSet(c.paramId, c.x, c.y, want);
      } catch (e) {
        report.errors.push(`restore ${formatAddrKey(addr)}: ${e instanceof Error ? e.message : String(e)}`);
      }
    }
    if (settleMs > 0) await new Promise((r) => setTimeout(r, settleMs));
  }
  let residual = 0;
  for (const [addr, c] of unrestorable) {
    const want = before.get(addr);
    if (want === undefined) continue;
    signal?.throwIfAborted();
    try {
      const got = await vdGet(c.paramId, c.x, c.y);
      if (got === want) continue;
      residual++;
      report.residual.push({
        name: c.name,
        paramId: c.paramId,
        x: c.x,
        y: c.y,
        expected: want,
        actual: got,
        pass: RESTORE_PASS,
      });
    } catch (e) {
      residual++;
      report.errors.push(`restore verify ${formatAddrKey(addr)}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  return residual;
}

/** Whether a cancelled run stopped before anything was written: in the capture or the
 *  reads ahead of the sweep. Asked of the phase rather than of `written`, which a cancel
 *  inside the first pass leaves at 0 after commands have gone out. */
export function cancelledBeforeWriting(report: SelfTestReport): boolean {
  return report.aborted && (report.phase === "connect" || report.phase === "readback");
}

/** Tally the per-guess verdicts in one pass (for the status line / report). */
export function summarizeVerdicts(unverified: UnverifiedFinding[]): {
  confirmed: number;
  roundTripped: number;
  refuted: number;
  untestable: number;
} {
  // No fall-through: "refuted" is counted only where the run said so. It was the
  // default once, which turned every other reason into a claim about the device.
  const counts = { confirmed: 0, roundTripped: 0, refuted: 0, untestable: 0 };
  for (const u of unverified) {
    if (u.outcome === "confirmed") counts.confirmed++;
    else if (u.outcome === "roundTripped") counts.roundTripped++;
    else if (u.outcome === "refuted") counts.refuted++;
    else counts.untestable++;
  }
  return counts;
}

/**
 * Render a report as human-readable Markdown an owner can save and send back to
 * confirm the unverified guesses. Leads with the per-guess verdicts (the point of
 * the run on URX22/URX44), then the device-fidelity residual and any issues. Pure.
 */
export function formatSelfTestReport(report: SelfTestReport): string {
  // The one shape a divergence is printed in, wherever it appears in the report.
  const wroteRead = (name: string, addr: string, wrote: number, read: number | null): string =>
    `${name} @ ${addr} — wrote ${wrote}, read ${read ?? "unreadable"}`;
  const mismatchLine = (m: SelfTestMismatch): string =>
    wroteRead(m.name, `${m.paramId}:${m.x}:${m.y}`, m.expected, m.actual);

  const lines: string[] = [];
  lines.push(`# URX self-test report — ${report.device || "(no device)"}`);
  lines.push("");
  lines.push(`- Result: ${report.ok ? "PASS" : "FAIL"} (phase: ${report.phase})`);
  const linkedPasses = report.passes - report.unlinkedPasses;
  lines.push(
    `- Captured groups: ${report.applied}; passes: ${report.passes} (${report.unlinkedPasses} with the MONO IN pairs unlinked, ${linkedPasses} STEREO-linked); commands written: ${report.written}`,
  );
  lines.push(
    report.phase === "refused"
      ? "- Restored: not applicable — the run refused to start and wrote nothing"
      : cancelledBeforeWriting(report)
        ? "- Restored: not applicable — the run was canceled before it wrote anything"
        : `- Restored: ${report.restored ? "yes" : `NO — ${report.restoreResidual} param(s) differ`}`,
  );

  if (report.unverified.length) {
    lines.push("");
    lines.push("## Unverified-guess verdicts");
    for (const u of report.unverified) {
      // One line per outcome, so a new reason has to be given one rather than
      // inheriting whatever the last branch happened to say.
      const verdict = {
        collision: "COULD NOT TEST — guessed id collides with a confirmed param (guess is wrong)",
        // Neither carries a divergence list: what a stopped pass left for this guess is
        // under "Not settled", where it is not standing next to a verdict. And only the
        // first names a cause, because only the first is about this guess's own address.
        unread: "COULD NOT TEST — one of its addresses could not be read, so nothing about it round-tripped",
        incomplete:
          "COULD NOT TEST — it differed in a pass that did not finish; the difference is under Not settled, and what stopped the run under Issues",
        unexercised:
          "COULD NOT TEST — not every one of its addresses was written and read back in a pass that completed (none on this model, or every pass that wrote them stopped, or the run was canceled)",
        confirmed: "CONFIRMED — round-tripped on the device",
        roundTripped:
          "ROUND-TRIPPED — every address held what was written, which cannot say which channel or meaning it reached; this does not confirm it",
        refuted: `REFUTED — ${u.mismatches.length} address(es) did not round-trip`,
      }[u.outcome];
      lines.push(`- **${u.label}** (${u.key}): ${verdict}`);
      for (const m of u.mismatches) lines.push(`  - ${mismatchLine(m)}`);
      if (u.howToSettle && u.outcome !== "refuted" && u.outcome !== "collision")
        lines.push(`  - NOT SETTLED BY THIS RUN — a round trip cannot answer it; to settle it, ${u.howToSettle}`);
    }
  }

  if (report.collisions.length) {
    lines.push("");
    lines.push("## Static collisions (refuted before any write)");
    for (const c of report.collisions) {
      lines.push(`- ${c.label}: param ${c.paramId} is already owned by ${c.confirmed}`);
    }
  }

  // Device divergence not attributable to an unverified guess — genuine fidelity issues,
  // and only from passes that completed. A pass that stopped differs in what is MISSING:
  // not the comparison (diffPlan reads every address whether or not one of them failed,
  // so a residual entry was read and did differ) but the convergence — the re-send and
  // re-read that a completed pass keeps doing until nothing differs. So these are not a
  // finding, and they are not "never looked at" either.
  const shown = report.residual.filter((r) => !r.unverifiedKey && !r.stoppedOn);
  // Every unsettled entry, guess-attributed or not: the verdicts above no longer carry
  // them, so this is the only place they appear at all.
  const unsettled = report.residual.filter((r) => r.stoppedOn);
  if (shown.length) {
    lines.push("");
    lines.push("## Other device divergence (confirmed params)");
    for (const m of shown) lines.push(`- ${passLabel(m.pass)} ${mismatchLine(m)}`);
  }
  if (unsettled.length) {
    lines.push("");
    lines.push(`## Not settled — ${unsettled.length} param(s) still differing when the run stopped`);
    lines.push("");
    lines.push(
      "The pass ended early (a refused write, or a read it could not make), so the loop never" +
        " finished converging these. Each of them was read and did differ at that point; what a" +
        " completed pass would have done next — re-send, re-read, until nothing differs — is what" +
        " did not happen, so none of this is a fidelity finding. Addresses the run could not read" +
        " AT ALL are not counted here: nothing was compared for them, and they are under Issues.",
    );
    for (const m of unsettled) {
      const guess = m.unverifiedKey ? `, guess ${m.unverifiedKey}` : "";
      lines.push(
        `- ${passLabel(m.pass)} ${m.name} @ ${m.paramId}:${m.x}:${m.y} — plan wanted ${m.expected}, device answered ${m.actual ?? "unreadable"} at the last read (the pass stopped on a ${m.stoppedOn === "write" ? "refused write" : "failed read"}${guess})`,
      );
    }
  }

  if (report.errors.length) {
    lines.push("");
    lines.push("## Issues (read/send failures)");
    for (const e of report.errors) lines.push(`- ${e}`);
  }

  // The time series behind each failing pass. Verbose on purpose: a residual line
  // names the end state, and the question a divergence raises — was it ever sent,
  // what else went out in the same round and in what order, how long did the device
  // have — is answerable only from the rounds themselves.
  for (const t of report.traces) {
    lines.push("");
    lines.push(
      t.pass === RESTORE_PASS
        ? "## Converge trace — restore"
        : `## Converge trace — pass ${t.pass} (pairs ${t.pass < report.unlinkedPasses ? "unlinked" : "STEREO-linked"})`,
    );
    lines.push("");
    lines.push("Captured value of each address that did not converge (the diff's starting point):");
    for (const b of t.baseline) lines.push(`- ${b.name} @ ${b.addr} = ${b.value}`);
    t.rounds.forEach((r, i) => {
      lines.push("");
      const found =
        r.reread === null ? "no re-read (the round stopped on a send failure)" : `${r.reread.length} still differing`;
      // `sent` is what the round ISSUED (roundCommands), which is what the trace is for.
      // On a round that stopped, that is not what the device saw, and the heading has to
      // stop saying it was.
      const stopped = r.reread === null;
      lines.push(`### Round ${i + 1} — ${stopped ? "issued" : "sent"} ${r.sent.length}, ${r.elapsedMs} ms, ${found}`);
      lines.push("");
      lines.push(stopped ? "Issued, in order — the tail after the refusal never left the app:" : "Sent, in order:");
      for (const c of r.sent) lines.push(`- ${c.name} @ ${formatAddrKey(cmdAddr(c))} = ${c.vdValue}`);
      if (r.reread?.length) {
        lines.push("");
        lines.push("Read back after the settle:");
        for (const d of r.reread) {
          lines.push(`- ${wroteRead(d.command.name, formatAddrKey(cmdAddr(d.command)), d.command.vdValue, d.current)}`);
        }
      }
    });
  }
  lines.push("");
  return lines.join("\n");
}
